import { useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { Package, CheckCircle, XCircle, User, Mail, Phone, Pencil, ShoppingBag, MapPin, Download, RotateCcw, ArrowRight, Loader2, CreditCard, AlertCircle, Landmark, Trash2, Wallet, Bell } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { ordersAPI, paymentsAPI, authAPI, creditAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { OrderProgressTracker } from '@/components/OrderProgressTracker';
import { CellphoneHint } from '@/components/CellphoneHint';
import { sanitizeCell } from '@/lib/phone';
import { normalizeStatus, normalizePayment, CANCELLABLE } from '@/lib/orderStatus';
import { formatUnitSize } from '@/lib/units';
import { money } from '@/lib/format';

export default function ProfilePage() {
  const { user, logout, refreshUser, loading: authLoading } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [busyOrder, setBusyOrder] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', surname: '', cellphone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [creditTxns, setCreditTxns] = useState([]);
  const [creditOpen, setCreditOpen] = useState(false);

  const isGoogleAccount = user?.auth_provider === 'google';

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      await authAPI.deleteAccount({
        confirm: deleteConfirm,
        password: isGoogleAccount ? undefined : deletePassword,
      });
      toast.success('Your account and personal data have been deleted.');
      await logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete your account.');
    } finally {
      setDeleting(false);
    }
  };

  const fetchOrders = () => {
    if (user) {
      ordersAPI.list()
        .then(r => setOrders(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    ordersAPI.list()
      .then(r => { if (active) setOrders(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    creditAPI.myTransactions()
      .then(r => { if (active) setCreditTxns(r.data.transactions || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [user]);

  // Human-readable label for each ledger entry type.
  const CREDIT_LABELS = {
    grant: 'Credit added',
    manual_adjustment: 'Adjustment',
    order_applied: 'Applied to order',
    order_reversal: 'Returned from cancelled order',
    refund_to_credit: 'Refund to credit',
    expiry: 'Expired',
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setCancelling(orderId);
    try {
      await ordersAPI.cancel(orderId);
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancelling(null);
    }
  };

  const handleInvoice = async (orderId) => {
    setBusyOrder(`invoice-${orderId}`);
    try {
      const res = await ordersAPI.downloadInvoice(orderId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Invoice_${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download invoice');
    } finally {
      setBusyOrder(null);
    }
  };

  const handleReorder = async (orderId) => {
    setBusyOrder(`reorder-${orderId}`);
    try {
      const res = await ordersAPI.reorder(orderId);
      const { available, unavailable } = res.data;
      available.forEach(it => addItem({ id: it.product_id, ...it }, it.quantity));
      if (available.length) {
        toast.success(`${available.length} item${available.length > 1 ? 's' : ''} added to cart`);
      }
      if (unavailable.length) {
        const names = unavailable.map(u => u.name).join(', ');
        toast.warning(`Not added: ${names}`);
      }
      if (available.length) navigate('/cart');
    } catch {
      toast.error('Reorder failed');
    } finally {
      setBusyOrder(null);
    }
  };

  const handlePayNow = async (orderId) => {
    setBusyOrder(`pay-${orderId}`);
    try {
      const res = await paymentsAPI.startPaystackCheckout(orderId);
      const url = res.data?.redirect_url;
      if (!url) {
        toast.error('Could not start payment. Please try again.');
        setBusyOrder(null);
        return;
      }
      window.location.assign(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payment');
      setBusyOrder(null);
    }
  };

  const openEditProfile = () => {
    setProfileForm({
      first_name: user?.first_name || '',
      surname: user?.surname || '',
      cellphone: user?.cellphone || user?.phone || '',
    });
    setEditingProfile(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await authAPI.updateProfile({
        first_name: profileForm.first_name,
        surname: profileForm.surname,
        cellphone: profileForm.cellphone,
      });
      await refreshUser();
      toast.success('Profile updated');
      setEditingProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const [savingNotify, setSavingNotify] = useState(false);
  const toggleNotifyOptIn = async (value) => {
    setSavingNotify(true);
    try {
      await authAPI.updateProfile({ notify_opt_in: value });
      await refreshUser();
      toast.success(value ? 'Subscribed to order-window alerts' : 'Unsubscribed from alerts');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update preference');
    } finally {
      setSavingNotify(false);
    }
  };

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" />;

  return (
    <div data-testid="profile-page" className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8 sm:mb-12 text-center sm:text-left">
          <div className="inline-flex items-center gap-3 mb-2 sm:mb-3">
            <div className="h-[2px] w-8 bg-gold rounded-full" />
            <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gold font-bold">My Account</p>
          </div>
          <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight">
            Profile & Orders
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="profile-card">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-teal/15 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <User className="h-6 w-6 sm:h-8 sm:w-8 text-teal" />
              </div>
              {editingProfile ? (
                <form onSubmit={handleSaveProfile} className="space-y-3 mt-2" data-testid="profile-edit-form">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="font-body text-xs text-foreground/70">First name</Label>
                      <Input
                        data-testid="profile-first-name-input"
                        value={profileForm.first_name}
                        onChange={e => setProfileForm(f => ({ ...f, first_name: e.target.value }))}
                        className="mt-1 rounded-lg bg-muted border-border font-body text-sm text-foreground"
                        placeholder="John"
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-body text-xs text-foreground/70">Surname</Label>
                      <Input
                        data-testid="profile-surname-input"
                        value={profileForm.surname}
                        onChange={e => setProfileForm(f => ({ ...f, surname: e.target.value }))}
                        className="mt-1 rounded-lg bg-muted border-border font-body text-sm text-foreground"
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-body text-xs text-foreground/70">Cellphone</Label>
                    <Input
                      data-testid="profile-cellphone-input"
                      type="tel"
                      inputMode="tel"
                      maxLength={16}
                      value={profileForm.cellphone}
                      onChange={e => setProfileForm(f => ({ ...f, cellphone: sanitizeCell(e.target.value) }))}
                      className="mt-1 rounded-lg bg-muted border-border font-body text-sm text-foreground"
                      placeholder="082 123 4567"
                      required
                    />
                    <CellphoneHint value={profileForm.cellphone} />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      variant="ctaPrimary"
                      data-testid="profile-save-btn"
                      disabled={savingProfile}
                      className="flex-1 rounded-lg font-body text-xs font-semibold py-4"
                    >
                      {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                    </Button>
                    <Button
                      type="button"
                      variant="ctaSecondary"
                      data-testid="profile-cancel-btn"
                      onClick={() => setEditingProfile(false)}
                      className="flex-1 rounded-lg font-body text-xs py-4"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <h2 className="font-heading text-lg sm:text-xl font-medium text-foreground text-center">{user.name}</h2>
                  <p className="font-body text-xs sm:text-sm text-foreground/50 text-center mt-1 flex items-center justify-center gap-1.5 sm:gap-2">
                    <Mail className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> {user.email}
                  </p>
                  {user.phone && (
                    <p className="font-body text-xs sm:text-sm text-foreground/50 text-center mt-1 flex items-center justify-center gap-1.5 sm:gap-2" data-testid="profile-phone">
                      <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> {user.phone}
                    </p>
                  )}
                  <div className="mt-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={openEditProfile}
                      data-testid="profile-edit-btn"
                      className="rounded-lg text-teal hover:bg-teal/10 font-body text-xs gap-1.5"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit profile
                    </Button>
                  </div>
                  {user.is_vip && (
                    <div className="mt-2 text-center">
                      <Badge className="bg-gold/15 text-gold border-gold/30 rounded-full px-3 sm:px-4 text-xs" variant="outline">VIP Customer</Badge>
                    </div>
                  )}
                  {/* Order-window-opening alerts opt-in. On by default; customers
                      can opt out here. Always visible so the preference is easy
                      to find. */}
                  <div className="mt-5 pt-4 border-t border-border/60" data-testid="notify-optin-section">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        <Bell className="h-4 w-4 text-teal mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-body text-xs font-semibold text-foreground">Order-window alerts</p>
                          <p className="font-body text-[11px] text-foreground/50 leading-snug">
                            Get a heads-up before a supplier's order window opens. On by default — switch off to opt out.
                          </p>
                        </div>
                      </div>
                      <Switch
                        data-testid="notify-optin-toggle"
                        checked={user.notify_opt_in !== false}
                        disabled={savingNotify}
                        onCheckedChange={toggleNotifyOptIn}
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-border grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="font-heading text-xl sm:text-2xl text-teal">{orders.length}</p>
                  <p className="font-body text-[10px] sm:text-xs text-foreground/50 uppercase tracking-wide">Orders</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-xl sm:text-2xl text-gold">
                    R{orders.reduce((sum, o) => sum + o.total, 0).toFixed(0)}
                  </p>
                  <p className="font-body text-[10px] sm:text-xs text-foreground/50 uppercase tracking-wide">Total Spent</p>
                </div>
              </div>
              {((user.credit_balance || 0) > 0 || creditTxns.length > 0) && (
                <div
                  className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-border"
                  data-testid="account-credit-panel"
                >
                  <div className="rounded-xl bg-gold/10 border border-gold/30 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <Wallet className="h-5 w-5 text-gold" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-[10px] uppercase tracking-wider text-foreground/50">Account Credit</p>
                      <p className="font-heading text-xl text-gold" data-testid="account-credit-balance">
                        R{money(Number(user.credit_balance || 0))}
                      </p>
                      <p className="font-body text-[11px] text-foreground/50 mt-0.5">
                        Automatically applied to your next order.
                      </p>
                    </div>
                  </div>
                  {creditTxns.length > 0 && (
                    <>
                      <button
                        type="button"
                        data-testid="credit-history-toggle"
                        onClick={() => setCreditOpen(o => !o)}
                        className="mt-3 -ml-2 rounded-md px-2 py-1 flex items-center gap-1.5 font-body text-xs text-teal hover:bg-teal/10 transition-colors"
                      >
                        <RotateCcw className="h-3 w-3" />
                        {creditOpen ? 'Hide credit activity' : 'View credit activity'}
                      </button>
                      {creditOpen && (
                        <div
                          className="mt-3 rounded-xl border border-border divide-y divide-border max-h-64 overflow-y-auto"
                          data-testid="credit-history-list"
                        >
                          {creditTxns.map((t, i) => (
                            <div key={i} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`credit-txn-${i}`}>
                              <div className="min-w-0">
                                <p className="font-body text-xs text-foreground">{CREDIT_LABELS[t.type] || t.type}</p>
                                <p className="font-body text-[10px] text-foreground/45 truncate">
                                  {t.reason || new Date(t.created_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Johannesburg' })}
                                </p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className={`font-body text-sm font-semibold ${t.amount >= 0 ? 'text-teal' : 'text-foreground/70'}`}>
                                  {t.amount >= 0 ? '+' : ''}R{money(Number(t.amount))}
                                </p>
                                <p className="font-body text-[10px] text-foreground/40">Bal: R{money(Number(t.balance_after))}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-border">
                <Button
                  onClick={logout}
                  variant="ctaSecondary"
                  data-testid="profile-logout-btn"
                  className="w-full rounded-lg font-body text-sm font-semibold py-4 sm:py-5"
                >
                  Sign Out
                </Button>
              </div>

              {/* POPIA — delete my data */}
              <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-border" data-testid="delete-account-section">
                {!deleteOpen ? (
                  <button
                    type="button"
                    data-testid="open-delete-account-btn"
                    onClick={() => setDeleteOpen(true)}
                    className="w-full flex items-center justify-center gap-2 rounded-lg py-2 text-destructive/80 hover:bg-destructive/10 hover:text-destructive font-body text-xs font-medium transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete my account & data
                  </button>
                ) : (
                  <form onSubmit={handleDeleteAccount} className="space-y-3" data-testid="delete-account-form">
                    <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/30 p-3">
                      <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <p className="font-body text-[11px] leading-relaxed text-destructive">
                        This permanently deletes your account and personal information. Past orders are
                        kept for accounting but fully anonymised. This cannot be undone.
                      </p>
                    </div>
                    {!isGoogleAccount && (
                      <div>
                        <Label className="font-body text-xs text-foreground/70">Confirm your password</Label>
                        <Input
                          data-testid="delete-password-input"
                          type="password"
                          value={deletePassword}
                          onChange={e => setDeletePassword(e.target.value)}
                          className="mt-1 rounded-lg bg-muted border-border font-body text-sm text-foreground"
                          placeholder="Your password"
                          required
                        />
                      </div>
                    )}
                    <div>
                      <Label className="font-body text-xs text-foreground/70">Type DELETE to confirm</Label>
                      <Input
                        data-testid="delete-confirm-input"
                        value={deleteConfirm}
                        onChange={e => setDeleteConfirm(e.target.value)}
                        className="mt-1 rounded-lg bg-muted border-border font-body text-sm text-foreground"
                        placeholder="DELETE"
                        required
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        variant="ctaDanger"
                        data-testid="confirm-delete-account-btn"
                        disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'DELETE'}
                        className="flex-1 rounded-lg font-body text-xs font-semibold py-4"
                      >
                        {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete forever'}
                      </Button>
                      <Button
                        type="button"
                        variant="ctaSecondary"
                        data-testid="cancel-delete-account-btn"
                        onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); setDeletePassword(''); }}
                        className="flex-1 rounded-lg font-body text-xs py-4"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Orders Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="font-heading text-xl sm:text-2xl font-medium text-foreground">Order History</h2>
              <span className="font-body text-xs sm:text-sm text-foreground/50">{orders.length} orders</span>
            </div>
            
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-28 sm:h-32 rounded-2xl bg-muted animate-pulse" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 sm:py-16 rounded-2xl bg-card border border-border" data-testid="no-orders">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <ShoppingBag className="h-6 w-6 sm:h-8 sm:w-8 text-teal/50" />
                </div>
                <p className="font-heading text-xl sm:text-2xl text-foreground/60 mb-2">No orders yet</p>
                <p className="font-body text-xs sm:text-sm text-foreground/40 mb-4 sm:mb-6">Start shopping to see your orders here</p>
                <Link to="/shop">
                  <Button variant="ctaPrimary" className="rounded-lg font-body text-sm font-semibold px-6 sm:px-8 py-4 sm:py-5">
                    Browse Products
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4" data-testid="orders-list">
                {orders.map(order => {
                  const status = normalizeStatus(order.status);
                  const canCancel = CANCELLABLE.includes(status);
                  const paymentStatus = normalizePayment(order.payment_status);
                  const isUnpaid = ['unpaid', 'payment_requested', 'failed'].includes(paymentStatus);
                  const isEft = order.payment_method === 'eft';
                  const isClosed = ['cancelled', 'closed', 'rejected'].includes(status);
                  // Type A payable from checkout; Type B only once payment requested.
                  const workflow = order.payment_workflow === 'after_confirmation' ? 'after_confirmation' : 'on_checkout';
                  const paymentRequested = paymentStatus === 'payment_requested' || paymentStatus === 'failed';
                  const canPay = isUnpaid && !isClosed && (workflow === 'on_checkout' || paymentRequested);
                  const awaitingConfirmation = isUnpaid && !isClosed && workflow === 'after_confirmation' && !paymentRequested;

                  // Group items by supplier, alphabetical.
                  const groups = {};
                  (order.items || []).forEach(it => {
                    const key = it.supplier_name || 'Unknown supplier';
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(it);
                  });
                  const groupedSuppliers = Object.keys(groups).sort((a, b) => a.localeCompare(b));

                  const orderDate = new Date(order.created_at).toLocaleDateString('en-ZA', {
                    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Africa/Johannesburg'
                  });

                  const isCancelled = ['cancelled', 'rejected'].includes(status);

                  return (
                    <div
                      key={order.id}
                      data-testid={`order-${order.id}`}
                      className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-card border border-border hover:border-teal/30 transition-colors"
                    >
                      {/* Header: order # + date + collection point on the left;
                          only the cancelled OR payment badge on the right. The
                          live pipeline status is reflected by the progress bar
                          below, so we no longer duplicate it as a pill. */}
                      <div className="flex items-start justify-between mb-4 sm:mb-5 gap-2">
                        <div className="min-w-0">
                          <p className="font-body text-[10px] sm:text-xs text-teal uppercase tracking-wider font-semibold mb-1">Order</p>
                          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-0.5">
                            <p
                              data-testid={`order-number-${order.id}`}
                              className="font-body text-sm sm:text-base font-bold text-foreground"
                            >
                              {order.order_number || `#${order.id.slice(0, 8)}`}
                            </p>
                            <p className="font-body text-[10px] sm:text-xs text-foreground/50">
                              {orderDate}
                            </p>
                            {order.collection_point_name && (
                              <span
                                data-testid={`order-collection-inline-${order.id}`}
                                className="font-body text-[10px] sm:text-xs text-foreground/50 inline-flex items-center gap-1"
                              >
                                <span className="text-foreground/30">·</span>
                                <MapPin className="h-3 w-3 text-teal" strokeWidth={2} />
                                {order.collection_point_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          {isCancelled ? (
                            <Badge
                              data-testid={`order-cancelled-badge-${order.id}`}
                              className="bg-red-900/30 text-red-400 border-red-700/40 rounded-full font-body text-[10px] sm:text-xs flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-0.5 sm:py-1"
                              variant="outline"
                            >
                              <XCircle className="h-4 w-4" strokeWidth={2} /> Cancelled
                            </Badge>
                          ) : (
                            <Badge
                              data-testid={`order-payment-status-${order.id}`}
                              className={
                                paymentStatus === 'paid'
                                  ? 'bg-teal/15 text-teal border-teal/40 rounded-full font-body text-[10px] sm:text-xs flex items-center gap-1 px-2 sm:px-3 py-0.5'
                                  : paymentStatus === 'refunded'
                                  ? 'bg-purple-900/30 text-purple-300 border-purple-700/40 rounded-full font-body text-[10px] sm:text-xs flex items-center gap-1 px-2 sm:px-3 py-0.5'
                                  : 'bg-red-900/20 text-red-300 border-red-700/40 rounded-full font-body text-[10px] sm:text-xs flex items-center gap-1 px-2 sm:px-3 py-0.5'
                              }
                              variant="outline"
                            >
                              {paymentStatus === 'paid' ? (
                                <><CheckCircle className="h-3 w-3" strokeWidth={2} /> Paid</>
                              ) : paymentStatus === 'refunded' ? (
                                <><RotateCcw className="h-3 w-3" strokeWidth={2} /> Refunded</>
                              ) : paymentStatus === 'payment_requested' ? (
                                <><AlertCircle className="h-3 w-3" strokeWidth={2} /> Payment Requested</>
                              ) : (
                                <><AlertCircle className="h-3 w-3" strokeWidth={2} /> {awaitingConfirmation ? 'Under Review' : 'Unpaid'}</>
                              )}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Full labelled progress tracker — only for non-cancelled orders */}
                      {!isCancelled && (
                        <div className="mb-5 sm:mb-6" data-testid={`order-tracker-${order.id}`}>
                          <OrderProgressTracker status={order.status} paymentStatus={paymentStatus} variant="full" />
                        </div>
                      )}

                      {/* Pay Now banner — sits BELOW the progress tracker */}
                      {canPay && (
                        <div data-testid={`unpaid-banner-${order.id}`} className="mb-4 p-2.5 sm:p-3 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {isEft ? (
                              <Landmark className="h-4 w-4 text-gold flex-shrink-0" strokeWidth={2} />
                            ) : (
                              <CreditCard className="h-4 w-4 text-gold flex-shrink-0" strokeWidth={2} />
                            )}
                            <p className="font-body text-[11px] sm:text-xs text-foreground/80">
                              {isEft ? 'Awaiting EFT' : 'Payment outstanding'} — <span className="text-gold font-semibold">R{money(order.total)}</span>
                            </p>
                          </div>
                          {isEft ? (
                            <Link to={`/eft/${order.id}`} data-testid={`view-eft-btn-${order.id}`}>
                              <Button
                                variant="ctaPrimary"
                                size="sm"
                                className="rounded-full text-xs gap-1.5 px-3 font-semibold flex-shrink-0"
                              >
                                <Landmark className="h-4 w-4" strokeWidth={2} />
                                Banking Details
                              </Button>
                            </Link>
                          ) : (
                            <Button
                              onClick={() => handlePayNow(order.id)}
                              disabled={busyOrder === `pay-${order.id}`}
                              data-testid={`pay-now-btn-${order.id}`}
                              variant="ctaPrimary"
                              size="sm"
                              className="rounded-full text-xs gap-1.5 px-3 font-semibold flex-shrink-0"
                            >
                              {busyOrder === `pay-${order.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" strokeWidth={2} />}
                              Pay Now
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Items grouped by supplier — supplier name 2x larger */}
                      <div className="mb-4 pb-4 border-b border-border/50 space-y-3" data-testid={`order-items-${order.id}`}>
                        {groupedSuppliers.map(supplierName => (
                          <div key={supplierName} data-testid={`order-supplier-group-${order.id}-${supplierName}`}>
                            <p className="font-heading text-base sm:text-xl tracking-[0.12em] text-gold font-medium mb-2">
                              {supplierName.toUpperCase()}
                            </p>
                            <div className="space-y-1">
                              {groups[supplierName].map((item, i) => (
                                <div key={item.product_id || i} className="flex justify-between items-center gap-2 pl-2 border-l border-gold/15">
                                  <span className="font-body text-xs sm:text-sm text-foreground/75 truncate">
                                    {item.name} <span className="text-foreground/40">×{item.quantity}</span>
                                    {formatUnitSize(item) && (
                                      <span className="ml-1.5 text-gold/80">· {formatUnitSize(item)}</span>
                                    )}
                                  </span>
                                  <span className="font-body text-xs sm:text-sm text-foreground/55 flex-shrink-0">
                                    R{money((item.price * item.quantity))}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Price breakdown so line items reconcile to the total */}
                      <div className="mb-4 pb-4 border-b border-border/50 space-y-1.5 font-body" data-testid={`order-breakdown-${order.id}`}>
                        <div className="flex justify-between text-xs sm:text-sm text-foreground/55">
                          <span>Subtotal</span>
                          <span>R{money((order.items_subtotal ?? (order.total - (order.delivery_total || 0) - (order.collection_fee || 0))))}</span>
                        </div>
                        <div className="flex justify-between text-xs sm:text-sm text-foreground/55">
                          <span>Delivery</span>
                          <span className={(order.delivery_total || 0) > 0 ? '' : 'text-foreground/35'}>R{money((order.delivery_total || 0))}</span>
                        </div>
                        <div className="flex justify-between text-xs sm:text-sm text-foreground/55">
                          <span>Collection Fee</span>
                          <span className={(order.collection_fee || 0) > 0 ? '' : 'text-foreground/35'}>R{money((order.collection_fee || 0))}</span>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                        <div>
                          <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.18em] text-foreground/50 font-semibold">
                            Total
                          </p>
                          <p
                            data-testid={`order-total-${order.id}`}
                            className="font-body text-lg sm:text-2xl font-bold text-gold leading-tight"
                          >
                            R{money(order.total)}
                          </p>
                          <p className="font-body text-[10px] sm:text-xs text-foreground/40 mt-0.5">
                            {order.items.length} item{order.items.length > 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex items-center flex-wrap gap-2">
                          <Button
                            onClick={() => handleInvoice(order.id)}
                            disabled={busyOrder === `invoice-${order.id}`}
                            data-testid={`order-invoice-btn-${order.id}`}
                            variant="ctaSecondary"
                            size="sm"
                            className="rounded-full text-xs gap-1.5 px-3"
                          >
                            {busyOrder === `invoice-${order.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={2} />}
                            Invoice
                          </Button>
                          <Button
                            onClick={() => handleReorder(order.id)}
                            disabled={busyOrder === `reorder-${order.id}`}
                            data-testid={`order-reorder-btn-${order.id}`}
                            variant="ctaSecondary"
                            size="sm"
                            className="rounded-full text-xs gap-1.5 px-3"
                          >
                            {busyOrder === `reorder-${order.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" strokeWidth={2} />}
                            Reorder
                          </Button>
                          <Link to={`/orders/${order.id}`} data-testid={`order-view-btn-${order.id}`}>
                            <Button
                              variant="ctaPrimary"
                              size="sm"
                              className="rounded-full text-xs gap-1.5 px-3"
                            >
                              View <ArrowRight className="h-4 w-4" strokeWidth={2} />
                            </Button>
                          </Link>
                          {canCancel && (
                            <Button
                              variant="ctaDanger"
                              size="sm"
                              onClick={() => handleCancel(order.id)}
                              disabled={cancelling === order.id}
                              data-testid={`cancel-order-${order.id}`}
                              className="rounded-full font-body text-xs px-3"
                            >
                              {cancelling === order.id ? 'Cancelling...' : 'Cancel'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
